import {injectable} from "inversify";
import {Country, Region} from "@/modules/base/entity";
import {CountryEnum, RegionEnum} from "@/modules/base/enums";

@injectable()
export default class WorldFactory {
  private async createCountries(region: Region, mapping: [CountryEnum, number, number | null][]) {
    for (const [name, population, level] of mapping) {
      const country = new Country();
      country.name = name;
      country.population = population;
      country.level = Math.round(Math.max(1, level ? 10 - level / 20 : 1));
      country.region = region;
      await country.save();
    }
  }

  public async generate() {
    console.debug('generate regions');

    const eu = new Region();
    eu.name = RegionEnum.EU;
    eu.percentageAbroad = 5;
    await eu.save();

    // todo add eu countries
    await this.createCountries(eu, [
      [CountryEnum.AL, 2.877797, 66],
      [CountryEnum.AD, 0.077265, null],
      [CountryEnum.AT, 8.917205, 23],
      [CountryEnum.BY, 9.452113, 80],
      [CountryEnum.BE, 11.632353, 1],
      [CountryEnum.BA, 3.280815, 57],
      [CountryEnum.BG, 6.948445, 67],
      [CountryEnum.HR, 4.105267, 15],
      [CountryEnum.CY, 1.21663, 131],
      [CountryEnum.CZ, 10.708981, 44],
      [CountryEnum.DK, 5.814461, 10],
      [CountryEnum.EE, 1.321977, 105],
      [CountryEnum.FI, 5.54072, 54],
      [CountryEnum.FR, 65.273511, 2],
      [CountryEnum.DE, 83.166711, 13],
      [CountryEnum.GR, 10.724599, 30],
      [CountryEnum.HU, 9.66035, 40],
      [CountryEnum.IS, 0.343353, 37],
      [CountryEnum.IE, 4.982907, 47],
      [CountryEnum.IT, 59.554023, 19],
      [CountryEnum.XK, 1.810463, 124],
      [CountryEnum.LV, 1.913416, 131],
      [CountryEnum.LI, 0.038849, null],
      [CountryEnum.LT, 2.794191, 131],
      [CountryEnum.LU, 0.63473, 96],
      [CountryEnum.MT, 0.514564, 175],
      [CountryEnum.MD, 4.023485, 137],
      [CountryEnum.MC, 0.039242, null],
      [CountryEnum.ME, 0.628066, 71],
      [CountryEnum.NL, 17.173006, 16],
      [CountryEnum.MK, 2.085051, 72],
      [CountryEnum.NO, 5.461432, 41],
      [CountryEnum.PL, 37.846611, 7],
      [CountryEnum.PT, 10.191409, 6],
      [CountryEnum.RO, 19.187706, 43],
      [CountryEnum.RU, 144.373535, 38],
      [CountryEnum.SM, 0.033864, null],
      [CountryEnum.RS, 8.737371, 22],
      [CountryEnum.SK, 5.459642, 33],
      [CountryEnum.SI, 2.146373, 62],
      [CountryEnum.ES, 47.329981, 4],
      [CountryEnum.SE, 10.160166, 21],
      [CountryEnum.CH, 8.71586, 13],
      [CountryEnum.UA, 43.733762, 29],
      [CountryEnum.GB, 68.207169, 7]
    ]);

    const sa = new Region();
    sa.name = RegionEnum.SA;
    sa.percentageAbroad = 10;
    await sa.save();

    await this.createCountries(sa, [
      [CountryEnum.AR, 45.292747, 9],
      [CountryEnum.BO, 11.939587, 71],
      [CountryEnum.BR, 213.993437, 2],
      [CountryEnum.BV, 0, null],
      [CountryEnum.CL, 19.458310, 8],
      [CountryEnum.CO, 51.007707, 10],
      [CountryEnum.EC, 17.643054, 60],
      [CountryEnum.FK, 0.003400, null],
      [CountryEnum.GF, 0.301790, null],
      [CountryEnum.GY, 0.794329, 122],
      [CountryEnum.PY, 7.356673, 45],
      [CountryEnum.PE, 33.358163, 29],
      [CountryEnum.SR, 0.609569, 132],
      [CountryEnum.UY, 3.469551, 22],
      [CountryEnum.VE, 28.851904, 28]
    ]);

    const na = new Region();
    na.name = RegionEnum.NA;
    na.percentageAbroad = 2;
    await na.save();

    await this.createCountries(na, [
      [CountryEnum.AI, 0.015046, null],
      [CountryEnum.AG, 0.097928, null],
      [CountryEnum.AW, 0.107310, null],
      [CountryEnum.BS, 0.398050, 151],
      [CountryEnum.BB, 0.287711, null],
      [CountryEnum.BZ, 0.408487, 166],
      [CountryEnum.BM, 0.063779, null],
      [CountryEnum.CA, 38.048738, 59],
      [CountryEnum.KY, 0.066497, null],
      [CountryEnum.CR, 5.213859, 50],
      [CountryEnum.CU, 11.312419, 182],
      [CountryEnum.CW, 0.164100, null],
      [CountryEnum.DM, 0.073925, null],
      [CountryEnum.DO, 10.998046, 86],
      [CountryEnum.SV, 6.486205, 131],
      [CountryEnum.GL, 0.056770, null],
      [CountryEnum.GD, 0.112003, null],
      [CountryEnum.GT, 18.238168, 130],
      [CountryEnum.HT, 11.402528, 88],
      [CountryEnum.HN, 10.746309, 103],
      [CountryEnum.JM, 2.961714, 50],
      [CountryEnum.MX, 130.222815, 12],
      [CountryEnum.MS, 0.004979, null],
      [CountryEnum.NI, 6.715346, 147],
      [CountryEnum.PA, 4.382721, 76],
      [CountryEnum.PR, 2.909641, null],
      [CountryEnum.KN, 0.053192, null],
      [CountryEnum.LC, 0.181889, null],
      [CountryEnum.PM, 0.005733, null],
      [CountryEnum.VC, 0.111212, null],
      [CountryEnum.TT, 1.399111, 103],
      [CountryEnum.TC, 0.038717, null],
      [CountryEnum.US, 332.915073, 10],
      [CountryEnum.VG, 0.029818, null],
      [CountryEnum.VI, 0.103574, null]
    ]);

    const af = new Region();
    af.name = RegionEnum.AF;
    af.percentageAbroad = 12;
    await af.save();

    await this.createCountries(af, [
      [CountryEnum.DZ, 44.786592, 32],
      [CountryEnum.AO, 33.643677, 117],
      [CountryEnum.BJ, 13.540827, 86],
      [CountryEnum.BW, 2.364887, 146],
      [CountryEnum.BF, 22.607352, 60],
      [CountryEnum.BI, 12.633858, 146],
      [CountryEnum.CM, 27.429933, 50],
      [CountryEnum.CV, 0.556857, 187],
      [CountryEnum.CF, 4.941553, 191],
      [CountryEnum.TD, 17.118107, 137],
      [CountryEnum.KM, 0.869601, 195],
      [CountryEnum.CD, 107.310685, 88],
      [CountryEnum.CG, 5.522167, 152],
      [CountryEnum.CI, 28.607224, 59],
      [CountryEnum.DJ, 1.014191, 188],
      [CountryEnum.EG, 104.258327, 45],
      [CountryEnum.GQ, 1.479927, 146],
      [CountryEnum.ER, 3.524746, 197],
      [CountryEnum.ET, 117.876261, 153],
      [CountryEnum.GA, 2.276787, 120],
      [CountryEnum.GM, 2.495698, 165],
      [CountryEnum.GH, 31.072940, 58],
      [CountryEnum.GN, 13.829908, 72],
      [CountryEnum.GW, 2.005756, 136],
      [CountryEnum.KE, 54.985698, 106],
      [CountryEnum.LS, 2.165531, 151],
      [CountryEnum.LR, 5.057681, 142],
      [CountryEnum.LY, 6.871287, 150],
      [CountryEnum.MG, 29.617270, 96],
      [CountryEnum.MW, 19.776309, 128],
      [CountryEnum.ML, 20.768986, 55],
      [CountryEnum.MR, 4.706650, 147],
      [CountryEnum.MU, 1.273433, 141],
      [CountryEnum.MA, 38.949013, 34],
      [CountryEnum.MZ, 31.255435, 119],
      [CountryEnum.NA, 2.540905, 133],
      [CountryEnum.NE, 24.206643, 152],
      [CountryEnum.NG, 211.400708, 31],
      [CountryEnum.CG, 5.522167, 152],
      [CountryEnum.RW, 13.078600, 126],
      [CountryEnum.ST, 0.220194, 196],
      [CountryEnum.SN, 17.630404, 22],
      [CountryEnum.SC, 0.099086, 188],
      [CountryEnum.SL, 7.924406, 164],
      [CountryEnum.SO, 16.978116, 198],
      [CountryEnum.ZA, 60.041994, 74],
      [CountryEnum.SS, 11.192390, null],
      [CountryEnum.SD, 45.744053, 136],
      [CountryEnum.TZ, 62.773070, 141],
      [CountryEnum.TG, 8.596558, 124],
      [CountryEnum.TN, 11.947595, 26],
      [CountryEnum.UG, 47.930438, 77],
      [CountryEnum.EH, 0.599712, null],
      [CountryEnum.ZM, 19.847082, 73],
      [CountryEnum.ZW, 14.934115, 115]
    ]);

    const as = new Region();
    as.name = RegionEnum.AS;
    as.percentageAbroad = 4;
    await as.save();

    await this.createCountries(as, [
      [CountryEnum.AF, 39.835429, 139],
      [CountryEnum.AM, 2.961994, 105],
      [CountryEnum.AZ, 10.233457, 109],
      [CountryEnum.BH, 1.753329, 99],
      [CountryEnum.BD, 165.657989, 184],
      [CountryEnum.BT, 0.771612, 187],
      [CountryEnum.BN, 0.434481, 191],
      [CountryEnum.KH, 16.718965, 167],
      [CountryEnum.CN, 1393.783836, 75],
      [CountryEnum.CY, 1.21663, 131],
      [CountryEnum.GE, 3.729635, 89],
      [CountryEnum.HK, 7.547652, 122],
      [CountryEnum.IN, 1393.409038, 108],
      [CountryEnum.ID, 276.361783, 170],
      [CountryEnum.IR, 85.147265, 22],
      [CountryEnum.IQ, 41.179603, 70],
      [CountryEnum.IL, 9.415262, 85],
      [CountryEnum.JP, 126.047954, 29],
      [CountryEnum.JO, 10.550445, 118],
      [CountryEnum.KZ, 19.169646, 123],
      [CountryEnum.KW, 4.328550, 150],
      [CountryEnum.KG, 6.608783, 121],
      [CountryEnum.LA, 7.463865, 180],
      [CountryEnum.LB, 6.847565, 89],
      [CountryEnum.MY, 32.385669, 159],
      [CountryEnum.MV, 0.543617, 150],
      [CountryEnum.MN, 3.272909, 186],
      [CountryEnum.MM, 54.409800, 138],
      [CountryEnum.NP, 29.609623, 170],
      [CountryEnum.KP, 25.778312, 112],
      [CountryEnum.OM, 5.173968, 92],
      [CountryEnum.PK, 225.199937, 200],
      [CountryEnum.PS, 5.308840, 98],
      [CountryEnum.PH, 111.046913, 127],
      [CountryEnum.QA, 2.832067, 102],
      [CountryEnum.SA, 35.340683, 70],
      [CountryEnum.SG, 5.896686, 157],
      [CountryEnum.KR, 51.834442, 40],
      [CountryEnum.LK, 21.803000, 156],
      [CountryEnum.SY, 17.108523, 79],
      [CountryEnum.TW, 23.608460, 125],
      [CountryEnum.TJ, 9.537645, 141],
      [CountryEnum.TH, 69.518553, 110],
      [CountryEnum.TL, 1.366499, 194],
      [CountryEnum.TR, 84.339067, 29],
      [CountryEnum.TM, 6.181190, 143],
      [CountryEnum.AE, 9.856612, 74],
      [CountryEnum.UZ, 34.866128, 89],
      [CountryEnum.VN, 98.399449, 93],
      [CountryEnum.YE, 30.129808, 143]
    ]);
  }
}
